import type { ConfigType } from '@plone/registry';
import cloneDeep from 'lodash/cloneDeep';
import {
  DefaultEdit,
  DefaultView,
} from '../../customizations/@eeacms/volto-tabs-block/components/templates/default';
import { ButtonsView, ButtonsEdit } from '../../components/Blocks/Buttons';
import {
  NewsletterView,
  NewsletterEdit,
} from '../../components/Blocks/Newsletter';
import IframeView from '../../components/Blocks/Iframe/View';
import IframeEdit from '../../components/Blocks/Iframe/Edit';
import BlockSettingsSchema from '@plone/volto/components/manage/Blocks/Block/Schema';
import {
  InfinityMarqueeView,
  InfinityMarqueeEdit,
} from '../../components/Blocks/InfinityMarquee';
import {
  LayeredContentView,
  LayeredContentEdit,
} from '../../components/Blocks/LayeredContent';
import { LeriView, LeriEdit } from '../../components/Blocks/Leri';
import { CTAView, CTAEdit } from '../../components/Blocks/CTA';
import {
  LocationsView,
  LocationsEdit,
} from '../../components/Blocks/Locations';
import {
  CRMBlockView,
  CRMBlockEdit,
  CRMBlockSchema,
} from '../../components/Blocks/CRMFormBlock';
import { KeyDataView, KeyDataEdit } from '../../components/Blocks/KeyData';
import {
  ApplicationView,
  ApplicationEdit,
} from '../../components/Blocks/Application';
import { FollowUsEdit, FollowUsView } from '../../components/Blocks/FollowUs';
import { IrfEditView, IrfView } from '../../components/Irf';
import { config as HTMLBlockConfig } from '../../customizations/volto/components/manage/Blocks/HTML';
import AnchorBlockView from '../../components/Blocks/Anchor/View';
import AnchorBlockEdit from '../../components/Blocks/Anchor/Edit';
import { accordionFaqSchemaEnhancer } from '../../components/Blocks/Accordion/schema';
import {
  TestimonialsView,
  TestimonialsEdit,
} from '../../components/Blocks/Testimonial';

// Import icons directly from Volto
import emailSVG from '@plone/volto/icons/email.svg';
import presentationSVG from '@plone/volto/icons/presentation.svg';
import interfaceSVG from '@plone/volto/icons/interface.svg';
import circleMenuSVG from '@plone/volto/icons/circle-menu.svg';
import applicationSVG from '@plone/volto/icons/application.svg';
import annotationSVG from '@plone/volto/icons/annotation.svg';
import imageFitSVG from '@plone/volto/icons/image-fit.svg';
import imageRightSVG from '@plone/volto/icons/image-right.svg';
import bellSVG from '@plone/volto/icons/bell.svg';
import mapSVG from '@plone/volto/icons/map.svg';
import rowSVG from '@plone/volto/icons/row.svg';
import propertiesSVG from '@plone/volto/icons/properties.svg';
import nibSVG from '@plone/volto/icons/nib.svg';
import megaphoneSVG from '@plone/volto/icons/megaphone.svg';
import userSVG from '@plone/volto/icons/user.svg';
import circleBottomSVG from '@plone/volto/icons/circle-bottom.svg';
import linkSVG from '@plone/volto/icons/link.svg';
import quoteSVG from '@plone/volto/icons/quote.svg';

/**
 * Configure content blocks
 * Follows SOLID principles by grouping content-related block configurations
 */
export const configureContentBlocks = (config: ConfigType) => {
  // Slate table block configuration
  config.blocks.blocksConfig.slateTable = {
    ...config.blocks.blocksConfig.slateTable,
    group: 'content',
  };

  // HTML block configuration
  config.blocks.blocksConfig.html = {
    ...config.blocks.blocksConfig.html,
  };

  // PDF viewer block configuration
  config.blocks.blocksConfig.pdf_viewer = {
    ...config.blocks.blocksConfig.pdf_viewer,
    icon: presentationSVG,
    group: 'content',
  };

  // Tabs block configuration
  config.blocks.blocksConfig.tabs_block = {
    ...config.blocks.blocksConfig.tabs_block,
    icon: interfaceSVG,
    group: 'content',
    variations: [
      {
        id: 'default',
        title: 'Default',
        isDefault: true,
        edit: DefaultEdit,
        view: DefaultView,
        // schemaEnhancer: DefaultEdit.schemaEnhancer,
      },
    ],
  };

  // Buttons block configuration
  config.blocks.blocksConfig.buttons = {
    id: 'buttons',
    title: 'Buttons',
    icon: circleMenuSVG,
    group: 'content',
    view: ButtonsView,
    edit: ButtonsEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Newsletter block configuration - using direct Volto icon
  config.blocks.blocksConfig.newsletter = {
    id: 'newsletter',
    title: 'Newsletter (CleverReach)',
    icon: emailSVG,
    group: 'content',
    view: NewsletterView,
    edit: NewsletterEdit,
    restricted: false,
    mostUsed: false,
  };

  // Iframe block configuration
  config.blocks.blocksConfig.iframe = {
    id: 'iframe',
    title: 'Embed',
    icon: applicationSVG,
    group: 'content',
    view: IframeView,
    edit: IframeEdit,
    schema: BlockSettingsSchema,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Infinity marquee block configuration
  config.blocks.blocksConfig.infinity_marquee = {
    id: 'infinity_marquee',
    title: 'Laufschrift',
    icon: annotationSVG,
    group: 'content',
    view: InfinityMarqueeView,
    edit: InfinityMarqueeEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Layered content block configuration
  config.blocks.blocksConfig.layered_content = {
    id: 'layered_content',
    title: 'Mehrschichtiger Inhalt',
    icon: imageFitSVG,
    group: 'content',
    view: LayeredContentView,
    edit: LayeredContentEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Leri block configuration
  config.blocks.blocksConfig.leri = {
    id: 'leri',
    title: 'Bild mit Text',
    icon: imageRightSVG,
    group: 'content',
    view: LeriView,
    edit: LeriEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
    allowedBlocks: ['slate', 'slateTable', 'heading', 'buttons'],
  };

  // CTA block configuration
  config.blocks.blocksConfig.cta_block = {
    id: 'cta_block',
    title: 'Call to Action',
    icon: bellSVG,
    group: 'content',
    view: CTAView,
    edit: CTAEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
    allowedBlocks: ['slate', 'slateTable', 'heading', 'buttons'],
  };

  // Locations block configuration
  config.blocks.blocksConfig.locations_block = {
    id: 'locations_block',
    title: 'Standorte',
    icon: mapSVG,
    group: 'content',
    view: LocationsView,
    edit: LocationsEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // CRM form block configuration
  config.blocks.blocksConfig.crmformblock = {
    id: 'crmformblock',
    title: 'CRM Formular',
    icon: rowSVG,
    group: 'content',
    view: CRMBlockView,
    edit: CRMBlockEdit,
    schema: CRMBlockSchema,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Key data block configuration
  config.blocks.blocksConfig.keydata = {
    id: 'keydata',
    title: 'Eckdaten',
    icon: propertiesSVG,
    group: 'content',
    view: KeyDataView,
    edit: KeyDataEdit,
    restricted: true,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Application block configuration
  config.blocks.blocksConfig.application = {
    id: 'application',
    title: 'Anmeldung',
    icon: nibSVG,
    group: 'content',
    view: ApplicationView,
    edit: ApplicationEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Follow us block configuration
  config.blocks.blocksConfig.followus = {
    id: 'followus',
    title: 'Follow Us',
    icon: megaphoneSVG,
    group: 'content',
    view: FollowUsView,
    edit: FollowUsEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // IRF block configuration
  config.blocks.blocksConfig.irf = {
    id: 'irf',
    title: 'IRF Inhalte',
    icon: userSVG,
    group: 'content',
    view: IrfView,
    edit: IrfEditView,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // HTML block configuration (override)
  config.blocks.blocksConfig.html = HTMLBlockConfig;

  // Testimonial block configuration
  // NOTE: Must be registered before accordion so it's included in the accordion's blocksConfig clone
  config.blocks.blocksConfig.testimonial = {
    id: 'testimonial',
    title: 'Testimonial',
    icon: quoteSVG,
    group: 'content',
    view: TestimonialsView,
    edit: TestimonialsEdit,
    restricted: false,
    sidebarTab: 1,
    enableStyling: false,
  };

  // Accordion block configuration
  config.blocks.blocksConfig.accordion = {
    ...config.blocks.blocksConfig.accordion,
    icon: circleBottomSVG,
    group: 'content',
    schemaEnhancer: accordionFaqSchemaEnhancer,
    blocksConfig: cloneDeep(config.blocks.blocksConfig),
    allowedBlocks: [
      'image',
      'listing',
      'video',
      'html',
      'teaser',
      'iframe',
      'slate',
      'slateTable',
      'heading',
      'pdf_viewer',
      'schemaForm',
      'form',
      'solarSearch',
      'imageslider',
      'testimonial',
      'newsletter',
      'buttons',
      'lecturers',
      'contacts',
      'specializations',
      'research_focus',
      'modules',
      'leri',
      'cta_block',
      'locations_block',
      'irf',
    ],
  };

  // Anchor block configuration
  config.blocks.blocksConfig.anchor = {
    id: 'anchor',
    title: 'Anchor',
    icon: linkSVG,
    group: 'content',
    view: AnchorBlockView,
    edit: AnchorBlockEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  return config;
};
